/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Helpers
import Page from '../../lib/page';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import {
  ECM_DOC_ID_KEY,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
} from '../constants/ecmFPTITagsKeys';
import { PAYPAL_OBJECTS_BASE_URL, VIDEO_EVENT_TYPES } from '../constants/UtilVariables';
import { getArticleVideoIframeSrcDoc, loadYTIframeScript } from '../../lib/article-helper';

const { PLAYED, PAUSED } = VIDEO_EVENT_TYPES;

class Article extends Component {
  static getMarkup(articleContent) {
    return { __html: articleContent };
  }

  // To form faq link for by FAQ id of solutionLink in the content
  static handleFAQClick(event) {
    const clickTarget = event.target;
    const id = clickTarget.id;

    // To check the click content is solutionLink
    if (id && clickTarget.name === 'solutionLink') {
      event.preventDefault();
      const target = clickTarget.target || '_self';
      // TODO: add article title into the getArticleLink function
      window.open(Page.getArticleLink(id), target);
    }
  }

  static handleKeyPress(event) {
    if (event.which === 13) {
      Article.handleFAQClick();
    }
  }

  static trackPlayPauseEvent(eventType, videoSrc, articleId, fptiDocId, fptiComponentIds) {
    // Add FPTI event for when video would be played/paused
    trackLink({
      id: 'articlePageVideo',
      link: `article-page-video-${eventType}|${videoSrc}`,
      hpid: articleId,
      help_doc_id: fptiDocId,
      help_component_ids: fptiComponentIds,
    });
  }

  // add the FPTI events for Play and Pause clicks for the videos
  static addFPTIsForVideoPlayPause(videoElement, articleId, fptiDocId, fptiComponentIds, videoSrc) {
    videoElement.addEventListener('play', () =>
      Article.trackPlayPauseEvent(PLAYED, videoSrc, articleId, fptiDocId, fptiComponentIds),
    );

    videoElement.addEventListener('pause', () =>
      Article.trackPlayPauseEvent(PAUSED, videoSrc, articleId, fptiDocId, fptiComponentIds),
    );
  }

  constructor(props) {
    super(props);
    this.contentElement = null;
  }

  componentDidMount() {
    this.parseArticleContent();
  }

  handleLinkClick(e, link) {
    const articleId = this.props.article.articleId;
    const href = link.getAttribute('href');

    if (!href) {
      e.preventDefault();
      trackLink({ id: this.props.pageName, link: `smarthelp-related-article|${articleId}|broken-link-clicked` });
    } else {
      trackLink({ id: this.props.pageName, link: `smarthelp-related-article|${articleId}|link-clicked|${href}` });
      if (Page.isAnchorLink(href) && !Page.isMPPEnable()) {
        setTimeout(() => Page.scrollDown(), 20);
      }
    }
  }

  // Remove img replacement to div due to icon breakline issue
  parseArticleContent() {
    const loadYTIframeAPI = (onScriptLoad) => {
      const isScriptAlreadyPresent = loadYTIframeScript();
      if (isScriptAlreadyPresent) {
        onScriptLoad();
        return;
      }
      window.onYouTubeIframeAPIReady = onScriptLoad;
    };
    const onPlayerStateChange = (event, videoSrc, articleId, fptiDocId, fptiComponentIds) => {
      // eslint-disable-next-line default-case
      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          Article.trackPlayPauseEvent(PLAYED, videoSrc, articleId, fptiDocId, fptiComponentIds);
          break;
        case window.YT.PlayerState.PAUSED:
          Article.trackPlayPauseEvent(PAUSED, videoSrc, articleId, fptiDocId, fptiComponentIds);
          break;
      }
    };
    const trackYTVideoEvents = (videos, articleId, fptiDocId, fptiComponentIds) => {
      videos.forEach((video) => {
        if (window.YT) {
          // eslint-disable-next-line no-new
          new window.YT.Player(video, {
            events: {
              onStateChange: (e) => onPlayerStateChange(e, video.src, articleId, fptiDocId, fptiComponentIds),
              onReady: () => {
                trackPage({
                  id: 'articlePageVideo',
                  link: `video-displayed|${video.src}`,
                  hpid: articleId,
                  help_doc_id: fptiDocId,
                  help_component_ids: fptiComponentIds,
                });
              },
            },
          });
        }
      });
    };
    // Adding setTimeout on top as tag element is not picked immediately safari and IE versions
    setTimeout(() => {
      if (!this.contentElement) {
        return;
      }

      // Link click Tracking in Article Component
      const contentLinks = this.contentElement.querySelectorAll('a[href]');
      Array.from(contentLinks).forEach((link) => {
        link.addEventListener('click', (e) => {
          const clickTarget = e.target;
          const { id } = clickTarget;

          // To check if the click content is solutionLink
          if (id && clickTarget.name === 'solutionLink') {
            // make the faqId the href for fpti usage
            link.href = id;
          }

          this.handleLinkClick(e, link);
        });
      });

      // warp image in the article content
      let imageTags = this.contentElement.getElementsByTagName('img');
      imageTags = Array.prototype.slice.call(imageTags);
      imageTags.forEach((tag) => {
        const divEl = document.createElement('img');
        divEl.setAttribute('src', tag.src);
        divEl.style.height = `${tag.height}px`;
        divEl.style.maxWidth = `${tag.width}px`;
        divEl.setAttribute('alt', tag.alt);
        divEl.className = 'okm-image';
        tag.parentNode.replaceChild(divEl, tag);
      });

      // Logic to add video event listeners for play and pause for the videos that are inside iframe.
      let iframeTags = this.contentElement.getElementsByTagName('iframe');
      iframeTags = Array.prototype.slice.call(iframeTags);

      const articleId = (this.props.article && this.props.article.articleId) || '';

      // get the ECM API tags from article data
      const fptiDocId = (this.props.article && this.props.article[ECM_DOC_ID_KEY]) || ECM_DOC_ID_DEFAULT_VALUE;
      const fptiComponentIds =
        (this.props.article && this.props.article[ECM_COMPONENT_IDS_KEY]) || ECM_COMPONENT_IDS_DEFAULT_VALUE;
      const ytVideos = [];
      iframeTags.forEach((tag) => {
        const videoEl = tag;
        videoEl.className = 'okm-paypal-iframe';

        if (tag.src && tag.src.includes('youtube')) {
          videoEl.setAttribute(
            'src',
            tag.src && `${tag.src.replace('youtube.com', 'youtube-nocookie.com')}?enablejsapi=1`,
          );
          ytVideos.push(videoEl);
          return;
        }

        // This should not be done ideally, we should get proper format from ECM to display video and track events
        if (videoEl.src && videoEl.src.startsWith(PAYPAL_OBJECTS_BASE_URL) && !videoEl.srcdoc) {
          videoEl.setAttribute('srcdoc', getArticleVideoIframeSrcDoc(videoEl));
        }

        tag.parentNode.replaceChild(videoEl, tag);

        videoEl.onload = function () {
          const iFrameDocument = videoEl.contentDocument;
          const videoSrc = videoEl.src;

          if (iFrameDocument) {
            const videoElement = iFrameDocument.getElementsByTagName('video')[0] || null;
            if (videoElement) {
              // set video element width to 100% to display videos appropriatly for all device resolutions
              videoElement.setAttribute('width', '100%');

              trackPage({
                id: 'articlePageVideo',
                link: `video-displayed|${videoSrc}`,
                hpid: articleId,
                help_doc_id: fptiDocId,
                help_component_ids: fptiComponentIds,
              });

              Article.addFPTIsForVideoPlayPause(videoElement, articleId, fptiDocId, fptiComponentIds, videoSrc);
            }
          }
        };
      });

      if (ytVideos.length) {
        loadYTIframeAPI(() => trackYTVideoEvents(ytVideos, articleId, fptiDocId, fptiComponentIds));
      }

      // Logic to add video event listeners for play and pause for the videos
      // that are coming directly with out iframe.
      let videoTags = this.contentElement.getElementsByTagName('video');
      videoTags = Array.prototype.slice.call(videoTags);

      videoTags.forEach((videoElement) => {
        const videoSrc = videoElement.currentSrc;

        trackPage({
          id: 'articlePageVideo',
          link: `video-displayed|${videoSrc}`,
          hpid: articleId,
        });

        // FPTI event listeners for play and pause
        Article.addFPTIsForVideoPlayPause(videoElement, articleId, null, null, videoSrc);
      });
    });
  }

  render() {
    const { article } = this.props;
    const articleContent = article && article.articleContent;
    const articleTitle = article && article.articleTitle;
    const tabAttribs = {
      className: this.props.className,
      role: 'tabpanel',
      tabIndex: '0',
    };

    return (
      <div {...tabAttribs}>
        <p className="title">{articleTitle}</p>
        <div className="contents" role="article">
          <p // eslint-disable-line jsx-a11y/no-static-element-interactions
            className="okm-content"
            dangerouslySetInnerHTML={Article.getMarkup(articleContent)} // eslint-disable-line react/no-danger
            ref={(el) => {
              this.contentElement = el;
            }}
            onClick={Article.handleFAQClick}
            onKeyPress={Article.handleKeyPress}
          />
        </div>
      </div>
    );
  }
}

Article.defaultProps = {
  article: undefined,
  className: 'article-component',
  pageName: 'contactUsPage',
};

Article.propTypes = {
  article: PropTypes.shape({
    articleId: PropTypes.string,
    articleTitle: PropTypes.string,
    topicId: PropTypes.string,
    articleContent: PropTypes.string,
  }),
  className: PropTypes.string,
  pageName: PropTypes.string,
};

export default Article;
